import React, { useEffect, useRef } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { formatDateLong } from '../../utils/calendarContacts';
import {
  SCHEDULE_CUES,
  getScheduleCue,
  upsertScheduleCue,
  seedScheduleCues,
} from '../../utils/scheduleCues';
import { formatTimeInputMask, parseScheduleTimeFields } from '../../utils/scheduleTimeUtils';
import { fieldWellStyle, webInputClassProps, V2_INPUT_WELL_CLASS } from '../../utils/transparentTextInputStyle';
import { parseScheduleText } from '../ScheduleEditor';
import DaySheetScheduleEditor from './DaySheetScheduleEditor';

function wellProps() {
  return webInputClassProps(V2_INPUT_WELL_CLASS);
}

function busCallPrefillActive(value, busCallTime) {
  const parsed = parseScheduleTimeFields(busCallTime);
  if (!parsed.time || !value?.time) return false;
  return value.time === parsed.time && value.period === parsed.period;
}

function CueGrid({ scheduleText, pal, isDarkMode, onPersist, timeFormat, canEdit, busCallTime }) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
      {SCHEDULE_CUES.map((cue) => {
        const value = getScheduleCue(scheduleText, cue.id);
        const prefilled = cue.id === 'busCall' && busCallPrefillActive(value, busCallTime);
        const display = timeFormat === '24-hour' && value.time
          ? value.time
          : (value.time ? `${value.time} ${value.period}` : '');
        return (
          <View key={cue.id} style={{ minWidth: 160, flexGrow: 1, flexBasis: 180 }}>
            <Text style={[styles.label, { color: pal.ink, marginBottom: 6, fontSize: 13 }]}>{cue.label}</Text>
            {canEdit ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <TextInput
                  value={value.time}
                  onChangeText={(t) => {
                    onPersist(upsertScheduleCue(scheduleText, cue.id, {
                      time: formatTimeInputMask(t),
                      period: value.period,
                    }));
                  }}
                  placeholder="—"
                  placeholderTextColor={pal.faint}
                  keyboardType="numbers-and-punctuation"
                  accessibilityLabel={`${cue.label} time`}
                  {...wellProps()}
                  style={[
                    fieldWellStyle(pal, isDarkMode),
                    {
                      flex: 1,
                      minWidth: 72,
                      color: pal.ink,
                      fontFamily: FONT.mono,
                      fontSize: 18,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 10,
                    },
                  ]}
                />
                <TouchableOpacity
                  onPress={() => {
                    onPersist(upsertScheduleCue(scheduleText, cue.id, {
                      time: value.time,
                      period: value.period === 'AM' ? 'PM' : 'AM',
                    }));
                  }}
                  accessibilityLabel={`${cue.label} ${value.period}. Tap to switch AM/PM`}
                  style={[
                    fieldWellStyle(pal, isDarkMode),
                    {
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingHorizontal: 10,
                      paddingVertical: 8,
                    },
                  ]}
                >
                  <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 13, fontWeight: '600' }}>
                    {value.period || 'AM'}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 18 }}>
                {display || '—'}
              </Text>
            )}
            {prefilled ? (
              <Text style={{ color: pal.teal, fontSize: 12, marginTop: 6, fontWeight: '600' }}>
                Prefilled from Tour Planner
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

export function MasterTourSchedulePage({
  scheduleText = '',
  venueName,
  cityLine,
  dateKey,
  isDarkMode,
  accent,
  onPersist,
  canEdit = true,
  busCallTime = '',
  timeFormat = '12-hour',
}) {
  const pal = palette(isDarkMode);
  const hue = accent || pal.teal;
  const seedKey = useRef('');
  const rows = parseScheduleText(scheduleText).lines || [];

  useEffect(() => {
    if (!onPersist || !dateKey) return;
    const key = `${dateKey}|${busCallTime || ''}`;
    if (seedKey.current === key) return;
    const seeded = seedScheduleCues(scheduleText || '', { busCallTime });
    seedKey.current = key;
    if (seeded !== String(scheduleText || '')) onPersist(seeded);
  }, [dateKey, busCallTime, onPersist, scheduleText]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48, width: '100%' }}>
      <View style={{ borderBottomWidth: 2, borderBottomColor: hue, paddingBottom: 14, marginBottom: 22 }}>
        <Text style={[styles.label, { color: pal.mute, marginBottom: 5 }]}>Schedule</Text>
        <Text style={[styles.heroSm, { color: pal.ink }]}>{venueName || 'Day schedule'}</Text>
        <Text style={{ color: pal.mute, marginTop: 4 }}>
          {[cityLine, formatDateLong(dateKey)].filter(Boolean).join(' · ') || 'Pick a show day.'}
        </Text>
      </View>

      <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line, marginBottom: 16 }]}>
        <Text style={[styles.label, { color: pal.mute, marginBottom: 6 }]}>Call times</Text>
        <Text style={{ color: pal.ink, fontSize: 15, fontWeight: '600', marginBottom: 14 }}>
          Doors · Set · Venue Curfew · GR Curfew · Guest List · Bus Call
        </Text>
        <CueGrid
          scheduleText={scheduleText}
          pal={pal}
          isDarkMode={isDarkMode}
          onPersist={onPersist}
          timeFormat={timeFormat}
          canEdit={canEdit && Boolean(onPersist)}
          busCallTime={busCallTime}
        />
      </View>

      <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
        <Text style={[styles.label, { color: pal.mute, marginBottom: 6 }]}>Day schedule</Text>
        <Text style={{ color: pal.mute, fontSize: 13, marginBottom: 14 }}>
          Per diems and settlement stay on Expenses. Delete a row here if it does not belong on the day.
        </Text>
        {canEdit && onPersist ? (
          <DaySheetScheduleEditor
            value={scheduleText || ''}
            onChange={(next) => onPersist(next)}
            pal={pal}
          />
        ) : rows.length ? rows.map((ln) => (
          <View key={ln.id} style={[styles.row, { borderTopColor: pal.lineSoft }]}>
            <Text style={{ width: 92, color: pal.ink, fontFamily: FONT.mono }}>
              {ln.kind === 'schedule' ? [ln.time, ln.period].filter(Boolean).join(' ') || '—' : ''}
            </Text>
            <Text style={{ flex: 1, color: pal.ink, fontWeight: ln.kind === 'schedule' ? '600' : '400' }}>
              {ln.kind === 'schedule' ? (ln.item || ln.notes || '') : (ln.text || '')}
            </Text>
          </View>
        )) : (
          <Text style={{ color: pal.mute }}>No schedule for this day yet.</Text>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  label: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase' },
  heroSm: { fontSize: 27, fontWeight: '700', letterSpacing: -0.4 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18 },
  row: { flexDirection: 'row', alignItems: 'baseline', gap: 12, paddingVertical: 8, borderTopWidth: 1 },
});
